import React from 'react';

import { Button, FormControlLabel } from '@material-ui/core';
import { FiAlertTriangle } from 'react-icons/fi';
import clsx from 'clsx';

import { AppCheckbox } from '@vyce/core/src/components/inputs';
import { PermissionRequirementPair } from '@vyce/core/src/api/types';

import { useRolesStyles } from '../styles';

interface Props {
  roleName: string;
  resourceLabel?: string;
  clearResourceOverrides: boolean;
  hasExceptions: boolean;
  conflicts: PermissionRequirementPair[];
  disabled?: boolean;
  resolveLabel: (systemName: string) => string;
  onClearResourceOverridesChange: (value: boolean) => void;
  onReset: () => void;
}

export const PermissionDiffBanner: React.FC<Props> = ({
  roleName,
  resourceLabel,
  clearResourceOverrides,
  hasExceptions,
  conflicts,
  disabled,
  resolveLabel,
  onClearResourceOverridesChange,
  onReset,
}) => {
  const classes = useRolesStyles();
  const kind = (resourceLabel || 'resources').toLowerCase();

  return (
    <>
      {!!conflicts.length && (
        <div className={clsx(classes.banner, classes.bannerError)}>
          <FiAlertTriangle size={15} />
          <div className={classes.bannerText}>
            These permissions can't be removed while others still need them:{' '}
            {conflicts
              .map(pair => `${resolveLabel(pair.permission)} needs ${resolveLabel(pair.requires)}`)
              .join('; ')}
            .
          </div>
        </div>
      )}

      <div className={clsx(classes.banner, classes.bannerWarning)}>
        <FiAlertTriangle size={15} />
        <div className={classes.bannerText}>
          These permissions differ from what the <b>{roleName}</b> role gives. They apply to this user only.
          {!!resourceLabel && (
            <>
              {' '}
              <FormControlLabel
                control={
                  <AppCheckbox
                    variant="rectangle"
                    color="primary"
                    disabled={disabled}
                    checked={clearResourceOverrides}
                    onChange={e => onClearResourceOverridesChange(e.target.checked)}
                  />
                }
                label={`Apply to all ${kind}`}
              />
              {clearResourceOverrides && hasExceptions && (
                <span> — this clears the per-{kind.replace(/s$/, '')} exceptions below.</span>
              )}
            </>
          )}
        </div>
        <Button size="small" disabled={disabled} onClick={onReset}>
          Reset to role defaults
        </Button>
      </div>
    </>
  );
};
